<template>
    <div class="onboard-wrapper">
        <div v-show="currentStep === 'verify-email'" class="verify-step">
            <div class="verify-email">
                <h1>Please verify your email</h1>
                <p>We just sent a six-digit verification code to {{ user.email }}. It will expire in one hour. Keep this window open while checking for your code.</p>
                <p>Enter your code below:</p>
                <p>
                    <digit-code
                        :digits="6"
                        :group="3"
                        :disabled="processing"
                        @entered="verifyEmail"
                        ref="digitCode"
                    />
                </p>
                <p class="small-text">
                    Didn't receive an email?
                    <a style="margin-left: 0.3em;" @click="sendVerificationEmail">Resend</a>
                </p>
                <p class="small-text">
                    <a class="subtle" @click="$store.dispatch('auth/logout')">Quit and log out</a>
                </p>
            </div>
        </div>

        <div v-if="currentStep !== 'verify-email'" class="step">
            <div v-show="currentStep === 'user-profile'" class="top user-profile">
                <div>
                    <h1>Welcome to {{ appName }}</h1>
                    <h3>Let's set up your profile.</h3>
                </div>
                <div>
                    <div class="split">
                        <form-input label="First Name" v-model="localUser.fname" :trim="true"/>
                        <form-input label="Last Name" v-model="localUser.lname" :trim="true"/>
                    </div>
                    <phone-number v-if="!user.org_id" label="Phone Number" v-model="userPhone"/>
                    <form-select
                        v-if="!user.org_id"
                        label="Role"
                        placeholder="Select Role"
                        v-model="localUser.organization_role"
                        width="100%"
                    >
                        <option>Owner</option>
                        <option>Executive/Director</option>
                        <option>Manager</option>
                        <option>Information Technology</option>
                        <option>Human Resources</option>
                        <option>Sales/Support</option>
                        <option>Other</option>
                    </form-select>
                    <form-input v-if="user.org_id" label="Email" :value="user.email" :disabled="true"/>
                    <div>
                        <p>By continuing, you agree to the <a href='https://kb.paperworkforms.com/terms-and-policies/terms-of-service' target='_blank'>terms of service</a>.</p>
                        <p>
                            <label>
                                <input type="checkbox" v-model="enableMarketing">
                                I wish to receive product updates and news about {{ appName }}.
                            </label>
                        </p>
                    </div>
                </div>
            </div>

            <div v-show="currentStep === 'org-settings'" class="top org-settings">
                <div>
                    <h1>Organization Information</h1>
                    <h3>Help us get to know your organization.</h3>
                </div>
                <div>
                    <form-input label="Organization Name" v-model="localOrg.name" :trim="true"/>
                    <form-subdomain
                        v-if="!skipSubdomain"
                        ref="subdomain"
                        v-model="localOrg.subdomain"
                        @status-change="(isValid) => isSubdomainValid = isValid"
                    />
                    <form-select label="Industry" v-model="localOrg.industry" width="100%">
                        <option>Agriculture &amp; Fishing</option>
                        <option>Art &amp; Entertainment</option>
                        <option>Charity &amp; Non-Profit</option>
                        <option>Construction</option>
                        <option>Education &amp; Research</option>
                        <option>Finance &amp; Insurance</option>
                        <option>Health Care</option>
                        <option>Manufacturing</option>
                        <option>Natural Resource Extraction</option>
                        <option>Professional &amp; Scientific Services</option>
                        <option>Public Administration</option>
                        <option>Real Estate</option>
                        <option>Retail &amp; Wholesale Trade</option>
                        <option>Sports &amp; Recreation</option>
                        <option>Tourism &amp; Hospitality</option>
                        <option>Transportation &amp; Warehousing</option>
                        <option>Other</option>
                    </form-select>
                    <form-select label="Number of Employees" v-model="localOrg.number_of_employees" width="100%">
                        <option>Less than 5</option>
                        <option>Between 5 and 20</option>
                        <option>Between 20 and 50</option>
                        <option>Between 50 and 100</option>
                        <option>More than 100</option>
                    </form-select>
                </div>
            </div>

            <div v-show="currentStep === 'org-region'" class="top org-region">
                <div>
                    <h1>Data Storage Zone</h1>
                    <h3>Where to store your {{ appName }} data.</h3>
                    <div class="bottom">
                        <div>
                            <span class="extra-info" @click="showRegionInfo">
                                <i class="far fa-info-circle"></i>
                                <span class="extra-info-title">Why does this matter?</span>
                            </span>
                        </div>
                        <div>
                            <span class="extra-info" @click="showCountryNotAvailable">
                                <i class="far fa-info-circle"></i>
                                <span class="extra-info-title">What if my country isn't available?</span>
                            </span>
                        </div>
                    </div>
                </div>
                <div>
                    <region-chooser v-model="localOrg.region_code"/>
                </div>
            </div>
            
            <div class="footer">
                <div>STEP {{ stepNum + 1 }} OF {{ stepCount }}</div>
                <a v-show="stepNum > 0" class="subtle" @click="prevStep">Previous</a>
                <button
                    v-show="stepNum + 1 < stepCount"
                    class="primary"
                    :disabled="!enableNextStep"
                    @click="nextStep"
                >Next</button>
                <submit-button
                    class="primary"
                    v-show="stepNum + 1 === stepCount"
                    :disabled="!enableNextStep"
                    :click="finish"
                >Begin</submit-button>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import RegionChooser from './RegionChooser';
    import DigitCode from './DigitCode';
    import PhoneNumber from './PhoneNumber';
    import FormSubdomain from './FormSubdomain';
    const cloneDeep = require('lodash/cloneDeep');
    const EMAIL_PROVIDERS = ['gmail', 'outlook', 'hotmail', 'sympatico', 'bell', 'rogers', 'cogeco', 'teksavvy', 'yahoo', 'aol', 'live', 'msn', 'comcast', 'wanadoo', 'orange', 'rediffmail', 'free', 'gmx', 'web', 'yandex', 'ymail', 'libero', 'uol', 'bol', 'mail', 'cox', 'sbcglobal', 'sfr', 'verizon', 'att', 'googlemail', 'ig', 'bigpond', 'terra', 'neuf', 'alice', 'rocketmail', 'laposte', 'facebook', 'bellsouth', 'charter', 'rambler', 'tiscali', 'shaw', 'sky', 'earthlink', 'optonline', 'freenet', 't-online', 'aliceadsl', 'virgilio', 'home', 'qq', 'telenet', 'me', 'tiscali', 'voila', 'planet', 'tin', 'ntlworld', 'arcor', 'frontiernet', 'hetnet', 'zonnet', 'club-internet', 'juno', 'optusnet', 'blueyonder', 'bluewin', 'skynet', 'windstream', 'mac', 'centurytel', 'chello', 'aim', 'guerrillamail'];
    import { constants } from '../utils/constants';
    const { SUBDOMAIN_MIN_LENGTH, SUBDOMAIN_MAX_LENGTH } = constants;

    export default {
        name: 'Onboard',
        components: { RegionChooser, DigitCode, PhoneNumber, FormSubdomain },
        data() {
            return {
                processing: false,
                currentStep: '',
                localUser: {},
                localOrg: {},
                userPhone: '',
                enableMarketing: false,
                isSubdomainValid: false,
            };
        },
        computed: {
            ...mapGetters({
                user: 'user/user',
                userExists: 'user/exists',
                org: 'org/org',
                orgExists: 'org/exists',
                appName: 'base/appName',
                appBranding: 'base/appBranding',
            }),
            steps() {
                let user = this.user;
                let org = this.org;
                return [
                    !user.is_email_verified ? 'verify-email' : false,
                    !user.fname || !user.lname || !user.has_agreed_to_terms ? 'user-profile' : false,
                    !user.org_id || !org.name || (!org.subdomain && !this.skipSubdomain) ? 'org-settings' : false,
                    !user.org_id || !org.region_code ? 'org-region' : false
                ].filter(val => val);
            },
            stepNum() {
                return this.steps.indexOf(this.currentStep);
            },
            stepCount() {
                return this.steps.length;
            },
            enableNextStep() {
                if (this.currentStep === 'user-profile') {
                    return this.localUser.fname &&
                        this.localUser.lname && (
                            this.user.org_id || (
                                this.userPhone &&
                                this.localUser.organization_role
                            )
                        );
                } else if (this.currentStep === 'org-settings') {
                    return this.localOrg.name &&
                        (this.isSubdomainValid || this.skipSubdomain) &&
                        this.localOrg.industry &&
                        this.localOrg.number_of_employees;
                } else if (this.currentStep === 'org-region') {
                    return this.localOrg.region_code;
                }
                return false;
            },
            skipSubdomain() {
                return this.appBranding && this.appBranding.domain;
            },
        },
        methods: {
            nextStep() {
                this.currentStep = this.steps[this.stepNum + 1];
            },
            prevStep() {
                this.currentStep = this.steps[this.stepNum - 1];
            },
            async verifyEmail(code) {
                this.processing = true;
                try {
                    await this.$api.get(`user/checkEmailVerification/${code}`);
                    await this.$store.dispatch('user/load');
                    this.nextStep();
                    this.$store.dispatch('notification/success', 'Email address verified successfully');
                } catch (e) {
                    let info = '';
                    if (e.details && e.details.attemptsRemaining !== undefined) {
                        let num = e.details.attemptsRemaining || 'no';
                        info = ` (${num} attempt${num === 1 ? '' : 's'} remaining)`;
                    } else if (e.details && e.details.lockoutRemaining !== undefined) {
                        let mins = Math.ceil(e.details.lockoutRemaining / 60000);
                        info = ` (you may try again in ${mins} minute${mins === 1 ? '' : 's'})`;
                    }
                    this.$store.dispatch('notification/set', {
                        message: `${e.message}${info}`,
                        type: 'error',
                        timeout: 4000
                    });
                } finally {
                    this.processing = false;
                    await this.$nextTick();
                    if (this.$refs.digitCode) this.$refs.digitCode.reset();
                }
            },
            async sendVerificationEmail() {
                try {
                    await this.$api.post('user/resendEmail');
                    this.$store.dispatch('notification/success', 'Verification email sent');
                } catch (err) {
                    this.$store.dispatch('notification/set', {
                        message: 'Error sending verification email',
                        type: 'error',
                        timeout: 2000
                    });
                }
            },
            async finish() {
                this.processing = true;
                this.localUser.phone_numbers[0] = { number: this.userPhone, type: 'default' };
                this.localUser.settings.enableMarketing = this.enableMarketing;
                this.localUser.has_agreed_to_terms = true;
                await this.$store.dispatch('user/onboard', {
                    user: this.localUser,
                    org: this.localOrg
                });
                this.$store.dispatch('notification/set', {
                    message: `Your account is now set up and you're ready to use ${this.appName}!`,
                    type: 'success',
                    timeout: 4000
                });
            },
            async fetchData() {
                // This component is included in App.vue, not in a view, therefore the user or org may not be loaded
                // yet when this is mounted, since the middleware is only guaranteed to complete before views render
                while (!this.userExists || (this.user.org_id && !this.orgExists)) {
                    await this.$timeout(200);
                }

                this.localUser = cloneDeep(this.user);
                if (this.localUser.phone_numbers.length) {
                    this.userPhone = this.localUser.phone_numbers[0].number;
                }
                this.localOrg = cloneDeep(this.org);
                if (!this.localOrg.subdomain && !this.skipSubdomain) {
                    let parts = this.localUser.email.split('@')[1].split('.');
                    let domain = parts[0].toLowerCase();
                    if (/^e?mail/.test(domain)) domain = parts[1].toLowerCase();
                    if (
                        domain.length >= SUBDOMAIN_MIN_LENGTH
                        && domain.length <= SUBDOMAIN_MAX_LENGTH
                        && !EMAIL_PROVIDERS.includes(domain)
                    ) {
                        this.$refs.subdomain.setInitial(true);
                        this.localOrg.subdomain = domain;
                        this.$refs.subdomain.validate();
                    }
                }
            },
            showRegionInfo() {
                this.$store.dispatch('dialog/set', {
                    type: 'info',
                    header: 'Why does this matter?',
                    body: `Every ${this.appName} customer is able to choose from one of our four Data Residency Zones, which determine where your ${this.appName} data is stored.<br><br>Data Residency Zones allow you to remain in compliance with data sovereignty laws and regulations in your country.`,
                    dialogProps: { showX: true }
                });
            },
            showCountryNotAvailable() {
                this.$store.dispatch('dialog/set', {
                    type: 'info',
                    header: `What if my country isn't available?`,
                    body: `<b>If You Do Not Require Data Residency</b><br>You can choose any Data Residency Zone. Most customers without data residency requirements will choose the United States, but you are free to choose whatever Data Residency Zone is closest to you or that you are most comfortable with.<br><br><b>If You Do Require Data Residency</b><br>If you are in an industry with data storage regulations and are from a country that is not listed, please contact us for options. Custom data residency zones are available for Enterprise customers if one of our hosting partners has facilities in your country.`,
                    dialogProps: { showX: true }
                });
            },
            selectRegion(code) {
                this.localOrg.region_code = code;
            }
        },
        async mounted() {
            await this.fetchData();
            this.currentStep = this.steps[0];
            this.$loading(false);
        }
    };
</script>

<style lang="scss" scoped>
    .onboard-wrapper {
        padding: 10px 20px;
        height: 400px;
    }

    .step {
        display: flex;
        flex-direction: column;
        height: 100%;

        > .top {
            flex-grow: 1;
            display: flex;
            overflow: hidden;

            > div:first-child {
                flex-grow: 1;
                display: flex;
                flex-direction: column;

                h1 {
                    margin-bottom: 0;
                }

                .bottom {
                    flex-grow: 1;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-end;
                    padding-bottom: 3em;

                    > div {
                        margin-top: 1em;
                    }
                }
            }

            > div:last-child {
                width: 400px;
                margin-left: 40px;
                display: flex;
                flex-direction: column;
                justify-content: space-evenly;
            }
        }

        > .footer {
            display: flex;
            align-items: center;
            @include button-group;

            > div:first-child {
                flex-grow: 1;
                font-weight: $bolder;
            }

            a {
                margin-right: 1.5em;
                font-size: 0.85em;
            }
        }
    }

    .verify-step {
        text-align: center;

        .verify-email {
            width: 425px;
            display: inline-block;
        }

        p {
            margin: 1.8em 0;
            line-height: 1.7em;
        }
    }

    .user-profile {
        a {
            white-space: nowrap;
        }

        label {
            display: flex;
            color: $color-black;
            font-weight: normal;

            input[type="checkbox"] {
                margin-right: 1em;
            }
        }
    }

    .org-settings {
        padding-bottom: 2em;
    }

    .org-region {
        .region-chooser {
            flex-grow: 1;
            margin: 2.5em 0 2em;
        }
    }

    .small-text {
        font-size: 0.85em;
    }

    .split {
        display: flex;
        
        > div {
            margin-left: 1.5em;

            &:first-child {
                margin-left: 0;
            }
        }
    }
</style>